import React, { FC } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

type Props = {
  icon: React.ReactNode;
  count: number | string;
  label: string;
  linkLabel: string;
  to: string;
  iconBg: string;
  iconColor: string;
};

const StatCard: FC<Props> = ({
  icon,
  count,
  label,
  linkLabel,
  to,
  iconBg,
  iconColor,
}) => {
  const history = useHistory();
  return (
    <Card type="button" onClick={() => history.push(to)}>
      <Top>
        <Label>{label}</Label>
        <IconSquare bg={iconBg} color={iconColor}>
          {icon}
        </IconSquare>
      </Top>
      <Count>{count}</Count>
      <Divider />
      <ViewLink>
        {linkLabel} <ArrowForwardIcon />
      </ViewLink>
    </Card>
  );
};

export default StatCard;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Label = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconSquare = styled.div<{ bg: string; color: string }>`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 20px;
  }
`;

const Count = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.1;
  letter-spacing: -0.01em;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 2px 0;
`;

const ViewLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--primary-600);
  font-size: 0.8125rem;
  font-weight: 600;
  transition: color 0.15s ease;

  svg {
    font-size: 14px;
    transition: transform 0.15s ease;
  }
`;

const Card = styled.button`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-xs);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  box-sizing: border-box;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
    box-shadow: var(--shadow-sm);
  }

  &:hover ${ViewLink} {
    color: var(--primary-700);
    svg {
      transform: translateX(2px);
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:active {
    transform: translateY(1px);
  }

  @media only screen and (max-width: 1366px) {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-radius: 10px;

    ${Top} {
      display: contents;
    }

    ${IconSquare} {
      order: 1;
      width: 32px;
      height: 32px;
      border-radius: 8px;

      svg {
        font-size: 16px;
      }
    }

    ${Label} {
      order: 2;
      font-size: 0.6875rem;
    }

    ${Count} {
      order: 3;
      font-size: 1.125rem;
      line-height: 1.2;
      margin-left: auto;
    }

    ${Divider} {
      display: none;
    }

    ${ViewLink} {
      order: 4;
      font-size: 0.75rem;
    }
  }
`;
